import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../../components/Header/Header";
import Footer from "../../../../components/Footer/Footer";
import IndentDetails from "../../IndentPage/ItemsPage/IndentDetails";
import SOSHItemsTable from "./SOSHItemsTable";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SOSHInvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { row: initialRow } = location.state || {};
  const [row, setRow] = useState(initialRow);

  const [items, setItems] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [error, setError] = useState(null);
  const [isEditable, setIsEditable] = useState(true);
  const currentUserId = useSelector((state) => state.auth.id);
  const [forceClosureStatus, setForceClosureStatus] = useState(false);
  const [indentStatus, setIndentStatus] = useState({
    l1a: row?.L1A || 0,
    l1: row?.L1 || 0,
    l2: row?.L2 || 0,
    po: row?.PO || 0,
    ph: row?.PH || 0,
    ir: row?.IR || 0,
    sh: row?.SH || 1,
    isClosed: false,
  });

  useEffect(() => {
    const checkIndentStatus = async () => {
      if (row && row.IndentID) {
        try {
          const statusResponse = await axios.get(
            `${API_BASE_URL}/irpage/indents/check-status/${row.IndentID}`
          );

          setIndentStatus((prevStatus) => ({
            ...prevStatus,
            isClosed: statusResponse.data.isClosed,
            sh: statusResponse.data.SH || prevStatus.sh,
          }));

          // If closed or sh is 0, fetch closed details
          if (statusResponse.data.isClosed || statusResponse.data.SH === 0) {
            const closedDetailsResponse = await axios.get(
              `${API_BASE_URL}/irpage/indents/closed-details/${row.IndentID}`
            );

            const closedItems = closedDetailsResponse.data.items.map(
              (item) => ({
                itemID: item.ItemID,
                itemName: item.ItemName,
                quantity: item.Quantity,
                quantityReceived: item.QuantityReceived,
                difference: item.Difference,
                stock: item.Stock,
                categoryID: item.CategoryID,
                remarks: item.Description,
                promisedDate: item.PromisedDate,
                unitname: item.UnitName,
                forceClosure: item.ForceClosure || 0,
              })
            );

            setItems(closedItems);
            setIsDraft(false);
          }
        } catch (error) {
          console.error("Error checking indent status:", error);
        }
      }
    };

    const fetchDraftItems = async () => {
      if (row && row.IndentID) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/shpage/indentitems`,
            {
              params: { indentId: row.IndentID },
            }
          );

          const draftItems = response.data.map((item) => ({
            itemID: item.ItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock,
            categoryID: item.CategoryID,
            remarks: item.Description,
            promisedDate: item.PromisedDate,
            total: item.Quantity * item.Stock,
            accountingUnitID: item.AccountingUnitID,
            forceClosure: item.ForceClosure || 0,
            ForceClosureStatus: item.ForceClosureStatus || 0,
          }));

          setItems(draftItems);
          setIsDraft(true);

          // Set forceClosureStatus based on ForceClosureStatus field
          const forceClosed = draftItems.length > 0 && draftItems[0].ForceClosureStatus === 1;
          setForceClosureStatus(forceClosed);
        } catch (error) {
          console.error("Error fetching draft items:", error);
          setError("Failed to load draft items");
        }
      }
    };

    const fetchIndentDetails = async () => {
      try {
        if (!row?.IndentID) return;

        const response = await axios.get(`${API_BASE_URL}/popage/indent`, {
          params: { indentId: row.IndentID },
        });
        if (response.data) {
          setRow((prev) => ({
            ...prev,
            ...response.data,
          }));
        }
      } catch (error) {
        console.error("Error fetching indent details:", error);
        setError("Failed to load indent details");
      }
    };
    fetchIndentDetails();
    checkIndentStatus();
    fetchDraftItems();
  }, [row]);

  const calculateTotalAmount = () => {
    return items.reduce((acc, item) => acc + item.total, 0).toFixed(2);
  };

  const handleIndentStatusChange = (newStatus) => {
    setIndentStatus((prevStatus) => ({
      ...prevStatus,
      ...newStatus,
    }));

    if (newStatus.isClosed) {
      navigate("/SOSHPage", {
        state: {
          message: "Indent Closed Successfully",
        },
      });
    }
  };

  const handleForceClosureStatusChange = (newStatus) => {
    setForceClosureStatus(newStatus);
  };

  const handleSubmit = async () => {
    try {
      const submissionData = {
        indentId: row.IndentID,
        userId: currentUserId,
        items: items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity,
          stock: item.stock,
          remarks: item.remarks,
          categoryID: item.categoryID,
          promisedDate: item.promisedDate,
          accountingUnitID: item.accountingUnitID,
        })),
      };

      const response = await axios.post(
        `${API_BASE_URL}/irpage/indents/submit`,
        submissionData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        setIsDraft(false);
        setIsEditable(false);

        navigate("/SOSHPage", {
          state: {
            message: `Indent submitted successfully. ${response.data.itemsInserted} items processed.`,
          },
        });
      }
    } catch (error) {
      console.error("Submission Error:", error);

      const errorMessage =
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message ||
        "Submission failed. Please try again.";

      setError(errorMessage);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <Header />
      <div className="container mx-auto">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold underline">Items Received</h3>
        </div>
        {row && (
          <>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <IndentDetails
              row={row}
              onUpdate={(updatedRow) => {
                setRow((prev) => ({
                  ...prev,
                  ...updatedRow,
                }));
              }}
            />{" "}
            <SOSHItemsTable
              indentID={row.IndentID}
              items={items}
              calculateTotalAmount={calculateTotalAmount}
              onSubmit={handleSubmit}
              onPrint={handlePrint}
              onIndentStatusChange={handleIndentStatusChange}
              onForceClosureStatusChange={handleForceClosureStatusChange}
              isClosed={indentStatus.isClosed}
              userId={currentUserId}
              forceClosureStatus={forceClosureStatus}
              indentStatus={{
                l1a: indentStatus.l1a,
                l1: indentStatus.l1,
                l2: indentStatus.l2,
                po: indentStatus.po,
                ph: indentStatus.ph,
                ir: indentStatus.ir,
                sh: indentStatus.sh,
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SOSHInvoicingPage;
